import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Box, Column, ConfirmationModal, Divider, FileDropzone, IconButton, Row, Spinner, Typography } from 'seagull';
import { Checkmark, ColorThumbDown, ColorThumbUp, Document, ExclamationMark, Import, ThumbDown, ThumbUp } from 'seagull/icons';
import { useLocaleKeys } from '~/i18n';
import { CircularProgress } from './CircularProgress';
import { useImportAgreement } from './hooks/useImportAgreement';
import s from './ImportAgreementModal.module.scss';
import { GetImportAgreementModalTID, STEP_INTERVAL_MS, SUCCESS_DELAY_MS } from './ImportAgreementModal.utils';
const ANALYZING_STEPS = [
    0,
    1,
    2
];
const progressPercent = (completedStep)=>{
    if (completedStep >= 2) return 100;
    if (completedStep === 1) return 66;
    if (completedStep === 0) return 33;
    return 16;
};
const ImportModalShell = (_0)=>{
    let { testIds, dismissModal, contentClassName } = _0, rest = _object_without_properties(_0, [
        "testIds",
        "dismissModal",
        "contentClassName"
    ]);
    return /*#__PURE__*/ _jsx(ConfirmationModal, _object_spread({
        width: "550px",
        skin: "transparent",
        testId: testIds.Root(),
        onCloseButtonClick: dismissModal,
        contentClassName: cn(s.shellContent, contentClassName)
    }, rest));
};
export const ImportAgreementModal = (param)=>{
    let { dismissModal, onSuccess, testId, stepIntervalMs = STEP_INTERVAL_MS, successDelayMs = SUCCESS_DELAY_MS } = param;
    const { ImportAgreementModalTestIds } = useMemo(()=>GetImportAgreementModalTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys();
    const { modalState, isUploading, fileName, errorReason, conversationId, extractionSuccess, processFile, submitFeedback, resetToUpload, notifySuccess } = useImportAgreement(ImportAgreementModalTestIds, onSuccess);
    if (modalState === 'upload') {
        return /*#__PURE__*/ _jsx(ImportModalShell, {
            testIds: ImportAgreementModalTestIds,
            dismissModal: dismissModal,
            showFooter: false,
            title: t.agreementImport.uploadModal.title(),
            content: /*#__PURE__*/ _jsxs(Column, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        className: s.subtitle,
                        variant: "paragraph2",
                        skin: "secondary",
                        children: t.agreementImport.uploadModal.subtitle()
                    }),
                    /*#__PURE__*/ _jsx(FileDropzone, {
                        accept: ".pdf",
                        disabled: isUploading,
                        testId: ImportAgreementModalTestIds.Dropzone(),
                        inputTestId: "".concat(ImportAgreementModalTestIds.Dropzone(), "-input"),
                        onFileSelected: processFile,
                        className: s.uploadDropzone,
                        renderContent: ()=>isUploading ? /*#__PURE__*/ _jsx(Spinner, {
                                testId: ImportAgreementModalTestIds.DropzoneSpinner(),
                                size: 40
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        skin: "secondary",
                                        className: s.uploadIconCircle,
                                        children: /*#__PURE__*/ _jsx(Import, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph2",
                                        skin: "primary",
                                        weight: "normal",
                                        children: t.agreementImport.uploadModal.dropzoneText()
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        className: s.hint,
                                        skin: "secondary",
                                        variant: "paragraph3",
                                        children: t.agreementImport.uploadModal.dropzoneHint()
                                    })
                                ]
                            })
                    })
                ]
            })
        });
    }
    if (modalState === 'analyzing') {
        return /*#__PURE__*/ _jsx(AnalyzingContent, {
            testIds: ImportAgreementModalTestIds,
            fileName: fileName,
            extractionSuccess: extractionSuccess,
            dismissModal: dismissModal,
            notifySuccess: notifySuccess,
            stepIntervalMs: stepIntervalMs,
            successDelayMs: successDelayMs
        });
    }
    if (modalState === 'error') {
        return /*#__PURE__*/ _jsx(ErrorContent, {
            testIds: ImportAgreementModalTestIds,
            errorReason: errorReason,
            conversationId: conversationId,
            submitFeedback: submitFeedback,
            resetToUpload: resetToUpload,
            dismissModal: dismissModal
        });
    }
    return null;
};
const AnalyzingContent = (param)=>{
    let { testIds, fileName, extractionSuccess, dismissModal, notifySuccess, stepIntervalMs, successDelayMs } = param;
    const { t } = useLocaleKeys();
    const [completedStep, setCompletedStep] = useState(-1);
    const successCalledRef = useRef(false);
    const stepLabels = [
        t.agreementImport.analyzingModal.step1(),
        t.agreementImport.analyzingModal.step2(),
        t.agreementImport.analyzingModal.step3()
    ];
    useEffect(()=>{
        if (extractionSuccess) {
            setCompletedStep(2);
            return;
        }
        const interval = setInterval(()=>{
            setCompletedStep((prev)=>{
                if (prev >= 2) {
                    clearInterval(interval);
                    return prev;
                }
                return prev + 1;
            });
        }, stepIntervalMs);
        return ()=>clearInterval(interval);
    }, [
        extractionSuccess
    ]);
    useEffect(()=>{
        if (extractionSuccess && completedStep >= 2 && !successCalledRef.current) {
            const timeout = setTimeout(()=>{
                successCalledRef.current = true;
                notifySuccess();
            }, successDelayMs);
            return ()=>clearTimeout(timeout);
        }
    }, [
        extractionSuccess,
        completedStep,
        notifySuccess
    ]);
    return /*#__PURE__*/ _jsx(ImportModalShell, {
        testIds: testIds,
        dismissModal: dismissModal,
        showFooter: false,
        title: "",
        cancelButtonDisabled: true,
        content: /*#__PURE__*/ _jsxs(Column, {
            className: s.analyzingContent,
            children: [
                /*#__PURE__*/ _jsx(CircularProgress, {
                    percent: progressPercent(completedStep)
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    testId: testIds.AnalyzingTitle(),
                    variant: "h5",
                    weight: "normal",
                    className: s.analyzingTitle,
                    children: t.agreementImport.analyzingModal.title()
                }),
                /*#__PURE__*/ _jsxs(Box, {
                    testId: testIds.FileNameChip(),
                    className: s.fileNameChip,
                    children: [
                        /*#__PURE__*/ _jsx(Document, {
                            size: 16
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            skin: "secondary",
                            children: fileName
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Divider, {
                    className: s.divider
                }),
                /*#__PURE__*/ _jsx(Column, {
                    className: s.stepsContainer,
                    children: ANALYZING_STEPS.map((stepIndex)=>{
                        const isComplete = stepIndex <= completedStep;
                        const isActive = stepIndex === completedStep + 1;
                        return /*#__PURE__*/ _jsxs(Box, {
                            testId: testIds.Step(String(stepIndex)),
                            className: s.step,
                            children: [
                                isComplete ? /*#__PURE__*/ _jsx(Box, {
                                    testId: testIds.StepCheckmark(String(stepIndex)),
                                    className: s.stepCheckmark,
                                    children: /*#__PURE__*/ _jsx(Checkmark, {
                                        size: 16
                                    })
                                }) : /*#__PURE__*/ _jsx(Box, {
                                    className: cn(s.stepDot, {
                                        [s.stepDotActive]: isActive
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    skin: isActive ? 'magical' : isComplete ? 'secondary' : 'disabled',
                                    children: stepLabels[stepIndex]
                                })
                            ]
                        }, stepIndex);
                    })
                })
            ]
        })
    });
};
const ErrorContent = (param)=>{
    let { testIds, errorReason, conversationId, submitFeedback, resetToUpload, dismissModal } = param;
    const { t } = useLocaleKeys();
    const [selectedRating, setSelectedRating] = useState(null);
    const { title, description } = getErrorText(t, errorReason);
    const submitSelectedFeedback = ()=>{
        if (selectedRating != null) submitFeedback(selectedRating);
    };
    const handleDismiss = ()=>{
        submitSelectedFeedback();
        dismissModal();
    };
    const handleRetry = ()=>{
        submitSelectedFeedback();
        resetToUpload();
    };
    return /*#__PURE__*/ _jsx(ImportModalShell, {
        testIds: testIds,
        dismissModal: handleDismiss,
        title: "",
        secondaryButtonLabel: t.agreementImport.errorModal.cancelButton(),
        secondaryButtonTestId: testIds.CancelButton(),
        onSecondaryButtonClick: handleDismiss,
        primaryButtonLabel: t.agreementImport.errorModal.retryButton(),
        primaryButtonTestId: testIds.RetryButton(),
        onPrimaryButtonClick: handleRetry,
        bi: {
            primaryButton: {
                name: 'import_agreement_retry'
            },
            secondaryButton: {
                name: 'import_agreement_cancel'
            }
        },
        content: /*#__PURE__*/ _jsxs(Column, {
            className: s.errorContent,
            children: [
                /*#__PURE__*/ _jsx(Box, {
                    className: s.errorIconContainer,
                    children: /*#__PURE__*/ _jsx(ExclamationMark, {})
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    testId: testIds.ErrorTitle(),
                    variant: "h6",
                    className: s.errorTitle,
                    children: title
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    testId: testIds.ErrorDescription(),
                    variant: "paragraph2",
                    skin: "secondary",
                    className: s.errorDescription,
                    children: description
                }),
                /*#__PURE__*/ _jsx(Divider, {
                    className: s.divider
                }),
                conversationId != null && /*#__PURE__*/ _jsxs(Box, {
                    testId: testIds.FeedbackRow(),
                    className: s.feedbackRow,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: "secondary",
                            children: t.agreementImport.errorModal.feedbackPrompt()
                        }),
                        /*#__PURE__*/ _jsxs(Row, {
                            className: s.thumbButtons,
                            gap: 4,
                            children: [
                                /*#__PURE__*/ _jsx(IconButton, {
                                    testId: testIds.ThumbsUpButton(),
                                    skin: "transparent",
                                    onClick: ()=>setSelectedRating('thumbsUp'),
                                    children: selectedRating === 'thumbsUp' ? /*#__PURE__*/ _jsx(ColorThumbUp, {}) : /*#__PURE__*/ _jsx(ThumbUp, {})
                                }),
                                /*#__PURE__*/ _jsx(IconButton, {
                                    testId: testIds.ThumbsDownButton(),
                                    skin: "transparent",
                                    onClick: ()=>setSelectedRating('thumbsDown'),
                                    children: selectedRating === 'thumbsDown' ? /*#__PURE__*/ _jsx(ColorThumbDown, {}) : /*#__PURE__*/ _jsx(ThumbDown, {})
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
};
const getErrorText = (t, errorReason)=>{
    switch(errorReason){
        case 'insufficient_data':
            return {
                title: t.agreementImport.errorModal.insufficientData.title(),
                description: t.agreementImport.errorModal.insufficientData.description()
            };
        case 'not_an_agreement':
            return {
                title: t.agreementImport.errorModal.notAnAgreement.title(),
                description: t.agreementImport.errorModal.notAnAgreement.description()
            };
        default:
            return {
                title: t.agreementImport.errorModal.generic.title(),
                description: t.agreementImport.errorModal.generic.description()
            };
    }
};
